import { useState, useEffect, lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useFcmToken } from "@/hooks/useFcmToken";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import SplashScreen from "@/components/SplashScreen";
import MaintenancePage from "@/components/MaintenancePage";
import BigNotificationOverlay from "@/components/BigNotificationOverlay";
import { DelayedSuspense } from "@/components/DelayedSuspense";
import { CustomerLayout } from "@/layouts/CustomerLayout";
import PageLoader from "@/components/PageLoader";
import { CartProvider } from "@/contexts/CartContext";
import posthog from 'posthog-js';
import { safeLocalStorage, safeSessionStorage } from "@/utils/storage";
import { supabase } from "@/integrations/supabase/client";
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

// Perfect UX: Preload specific Skeletons so they render INSTANTLY while lazy chunks download
import SmartSkeleton from "@/components/SmartSkeleton";
import ServiceDetailSkeleton from "@/components/ServiceDetailSkeleton";
import BookingPageSkeleton from "@/components/booking/BookingPageSkeleton";
import SearchPageSkeleton from "@/components/customer/SearchPageSkeleton";
import BookingsHistorySkeleton from "@/components/customer/BookingsHistorySkeleton";
import ProfileSkeleton from "@/components/customer/ProfileSkeleton";

// Initialize PostHog for production analytics (only when real key is set)
const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
if (globalThis.window !== undefined && import.meta.env.PROD && posthogKey && posthogKey !== 'phc_placeholder') {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
  });
}

// Eager-loaded core user-flow pages (instant navigation, NO spinner flicker)
import Login from "./pages/Login";
import Index from "./pages/Index";

// Lazy-loaded secondary pages
const SearchPage = lazy(() => import("./pages/SearchPage"));
const CartPage = lazy(() => import("./pages/CartPage"));
const Profile = lazy(() => import("./pages/Profile"));
const BookingsHistory = lazy(() => import("./pages/BookingsHistory"));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const SubServiceDetail = lazy(() => import('./pages/SubServiceDetail'));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const WorkerRegister = lazy(() => import("./pages/WorkerRegister"));
const WorkerDashboard = lazy(() => import("./pages/WorkerDashboard"));
const MyReviews = lazy(() => import("./pages/MyReviews"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const HelpSupport = lazy(() => import("./pages/HelpSupport"));
const FavoritesPage = lazy(() => import("./pages/FavoritesPage"));
const TermsPrivacy = lazy(() => import("./pages/TermsPrivacy"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AccountDeletion = lazy(() => import("./pages/AccountDeletion"));
const PrivacyPolicyPublic = lazy(() => import("./pages/public/PrivacyPolicy"));

// Admin pages lazy-loaded
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminWorkers = lazy(() => import("./pages/admin/AdminWorkers"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminCities = lazy(() => import("./pages/admin/AdminCities"));
// AdminPlatformFees removed - unified into AdminPartnerSettlements
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminMapSettings = lazy(() => import("./pages/admin/AdminMapSettings"));
const AdminSocialMedia = lazy(() => import("./pages/admin/AdminSocialMedia"));
const AdminCustomerValue = lazy(() => import("./pages/admin/AdminCustomerValue"));
const AdminProfileSettings = lazy(() => import("./pages/admin/AdminProfileSettings"));
const AdminSOSHistory = lazy(() => import("./pages/admin/AdminSOSHistory"));
const AdminAppContent = lazy(() => import("./pages/admin/AdminAppContent"));
const AdminPhoneAuth = lazy(() => import("./pages/admin/AdminPhoneAuth"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminStoreConfig = lazy(() => import("./pages/admin/AdminStoreConfig"));
const AdminBugReports = lazy(() => import("./pages/admin/AdminBugReports"));
const AdminBilling = lazy(() => import("./pages/admin/AdminBilling"));
const AdminCMS = lazy(() => import("./pages/admin/AdminCMS"));
const AdminPartnerSettlements = lazy(() => import("./pages/admin/AdminPartnerSettlements"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 15, // 15 minutes for million-user scaling
      gcTime: 1000 * 60 * 60 * 24, // Keep in memory for 24 hours
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
    },
  },
});

// Implementation of LocalStorage Persistence (M-User Grade)
const PERSIST_KEY = 'fixeveryday_query_cache_v3';
let saveTimeout: ReturnType<typeof setTimeout> | null = null;

// Sensitive query keys that MUST NOT be persisted to localStorage (shared-computer risk)
const SENSITIVE_KEYS = ['bookings', 'profile', 'profiles', 'saved_addresses', 'notifications', 'reviews', 'workers', 'partner_bills', 'work_proofs'];

const saveCache = () => {
  if (saveTimeout) clearTimeout(saveTimeout);
  
  saveTimeout = setTimeout(() => {
    try {
      const cache = queryClient.getQueryCache().getAll()
        .filter(query => {
          if (query.state.status !== 'success' || !query.state.data) return false;
          // Skip any query whose key contains sensitive identifiers
          const keyStr = JSON.stringify(query.queryKey).toLowerCase();
          return !SENSITIVE_KEYS.some(k => keyStr.includes(k));
        })
        .map(query => ({
          key: query.queryKey,
          data: query.state.data
        }));
      if (cache.length > 0) {
        safeLocalStorage.setItem(PERSIST_KEY, JSON.stringify(cache));
      }
    } catch (e) {
      console.warn('Persistence failed:', e);
    }
  }, 2000); // Throttled to 2 seconds for million-user scaling
};

const loadCache = () => {
  try {
    const saved = safeLocalStorage.getItem(PERSIST_KEY);
    if (!saved) return;
    const cache = JSON.parse(saved);
    if (Array.isArray(cache)) {
      cache.forEach((item: { key: string[]; data: unknown }) => {
        queryClient.setQueryData(item.key, item.data);
      });
    }
  } catch (e) {
    console.error('Cache hydration failed', e);
  }
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  // Only show PageLoader while Supabase is resolving the session (max ~300ms on first load).
  // Do NOT gate on is_logging_in — that flag can be stale and causes extra spinner flashes.
  if (loading) return <PageLoader />;
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  // Show loader while auth + admin check resolves (JWT-based, very fast)
  if (authLoading || adminLoading) return <PageLoader text="" />;
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) return <Navigate to="/" replace />;
  
  return <>{children}</>;
};

const MaintenanceGuard = ({ children }: { children: React.ReactNode }) => {
  const { isMaintenanceMode, loading } = useMaintenanceMode();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  if (loading || adminLoading) return <>{children}</>;
  // Admins bypass maintenance mode
  if (isMaintenanceMode && !isAdmin) return <MaintenancePage />;
  return <>{children}</>;
};

const DashboardRedirector = () => {
  const { user, isAdmin, isWorker, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Only run if not loading, user exists, and we haven't checked since the app mounted
    if (loading || !user || checked) return;

    const pathname = location.pathname;
    const hasRedirected = safeSessionStorage.getItem('initial_redirect_done');
    
    if (hasRedirected) {
      setChecked(true);
      return;
    }

    if (isAdmin && !pathname.startsWith('/admin')) {
      console.log('Redirecting to admin dashboard');
      safeSessionStorage.setItem('initial_redirect_done', 'true');
      setChecked(true);
      navigate('/admin', { replace: true });
    } else if (isWorker && pathname === '/') {
      // Verify the worker record actually exists in DB before redirecting.
      // Admin may have deleted the partner but JWT still has stale is_worker claim.
      supabase.from('workers').select('id').eq('user_id', user.id).maybeSingle().then(({ data }) => {
        safeSessionStorage.setItem('initial_redirect_done', 'true');
        setChecked(true);
        if (data) {
          console.log('Redirecting to worker dashboard');
          navigate('/worker/dashboard', { replace: true });
        } else {
          // Worker record deleted — refresh session to clear stale JWT claim
          console.log('Worker record not found, clearing stale is_worker claim');
          supabase.auth.refreshSession();
          // Stay on customer home
        }
      });
    } else {
      setChecked(true);
    }
  }, [user, isAdmin, isWorker, loading, navigate, location, checked]);

  return null;
};

const FCMHandler = () => {
  useFcmToken(import.meta.env.VITE_FIREBASE_VAPID_KEY); // VAPID KEY
  return null;
};



const AppRoutes = () => {
  const isOAuthCallback = globalThis.location.hash.includes('access_token') || globalThis.location.hash.includes('error_description');

  const [showSplash, setShowSplash] = useState(() => {
    if (isOAuthCallback) return false;
    // Splash only shows if it hasn't been shown in this SESSION
    if (safeSessionStorage.getItem('splashShown')) return false;
    return true;
  });
  
  useEffect(() => {
    if (!showSplash) return;
    const timer = setTimeout(() => {
      setShowSplash(false);
      safeSessionStorage.setItem('splashShown', '1');
    }, 2200);
    return () => clearTimeout(timer);
  }, [showSplash]);

  // Splash is ONLY controlled by showSplash state. Auth loading is handled by ProtectedRoute.
  // This prevents the splash re-triggering when auth.loading flips from true→false after the
  // SplashScreen has already been shown and dismissed in this session.
  const splashVisible = showSplash;

  return (
    <>
      <SplashScreen show={splashVisible} />
      {!splashVisible && (
        <DelayedSuspense>
          <DashboardRedirector />
          <FCMHandler />
          <BigNotificationOverlay />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Public SEO pages */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/account-deletion" element={<AccountDeletion />} />
            <Route path="/delete-account" element={<AccountDeletion />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPublic />} />

            {/* Customer App Shell - Persistent Bottom Nav */}
            <Route element={<CustomerLayout />}>
              <Route path="/" element={<ProtectedRoute><MaintenanceGuard><Index /></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/search" element={<ProtectedRoute><MaintenanceGuard><Suspense fallback={<SmartSkeleton delay={0}><SearchPageSkeleton /></SmartSkeleton>}><SearchPage /></Suspense></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/bookings" element={<ProtectedRoute><MaintenanceGuard><Suspense fallback={<SmartSkeleton delay={0}><BookingsHistorySkeleton /></SmartSkeleton>}><BookingsHistory /></Suspense></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><MaintenanceGuard><Suspense fallback={<SmartSkeleton delay={0}><ProfileSkeleton /></SmartSkeleton>}><Profile /></Suspense></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/my-reviews" element={<ProtectedRoute><MaintenanceGuard><MyReviews /></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><MaintenanceGuard><SettingsPage /></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/favorites" element={<ProtectedRoute><MaintenanceGuard><FavoritesPage /></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/help" element={<ProtectedRoute><MaintenanceGuard><HelpSupport /></MaintenanceGuard></ProtectedRoute>} />
              <Route path="/terms" element={<TermsPrivacy />} />
            </Route>

            {/* Non-AppShell Customer Routes */}
            <Route path="/service/:slug/detail/:subServiceId" element={<ProtectedRoute><MaintenanceGuard><Suspense fallback={<SmartSkeleton delay={0}><ServiceDetailSkeleton /></SmartSkeleton>}><SubServiceDetail /></Suspense></MaintenanceGuard></ProtectedRoute>} />
            <Route path="/cart" element={<ProtectedRoute><MaintenanceGuard><CartPage /></MaintenanceGuard></ProtectedRoute>} />
            <Route path="/service/:slug" element={<ProtectedRoute><MaintenanceGuard><Suspense fallback={<SmartSkeleton delay={0}><ServiceDetailSkeleton /></SmartSkeleton>}><ServiceDetail /></Suspense></MaintenanceGuard></ProtectedRoute>} />
            <Route path="/book/:serviceSlug/:subServiceId" element={<ProtectedRoute><MaintenanceGuard><Suspense fallback={<SmartSkeleton delay={0}><BookingPageSkeleton /></SmartSkeleton>}><BookingPage /></Suspense></MaintenanceGuard></ProtectedRoute>} />
            <Route path="/legal/:slug" element={<LegalPage />} />

            {/* Worker - wrapped with MaintenanceGuard */}
            <Route path="/worker/register" element={<ProtectedRoute><MaintenanceGuard><WorkerRegister /></MaintenanceGuard></ProtectedRoute>} />
            <Route path="/worker/dashboard" element={<ProtectedRoute><MaintenanceGuard><WorkerDashboard /></MaintenanceGuard></ProtectedRoute>} />

            {/* Admin - NOT wrapped with MaintenanceGuard (admins always have access) */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
            <Route path="/admin/services" element={<AdminRoute><AdminServices /></AdminRoute>} />
            <Route path="/admin/workers" element={<AdminRoute><AdminWorkers /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
            <Route path="/admin/cities" element={<AdminRoute><AdminCities /></AdminRoute>} />
            <Route path="/admin/platform-fees" element={<Navigate to="/admin/partner-settlements" replace />} />
            <Route path="/admin/app-content" element={<AdminRoute><AdminAppContent /></AdminRoute>} />
            <Route path="/admin/content" element={<AdminRoute><AdminContent /></AdminRoute>} />
            <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
            <Route path="/admin/map-settings" element={<AdminRoute><AdminMapSettings /></AdminRoute>} />
            <Route path="/admin/social-media" element={<AdminRoute><AdminSocialMedia /></AdminRoute>} />
            <Route path="/admin/customer-value" element={<AdminRoute><AdminCustomerValue /></AdminRoute>} />
            <Route path="/admin/profile-strength" element={<AdminRoute><AdminProfileSettings /></AdminRoute>} />
            <Route path="/admin/sos" element={<AdminRoute><AdminSOSHistory /></AdminRoute>} />
            <Route path="/admin/phone-auth" element={<AdminRoute><AdminPhoneAuth /></AdminRoute>} />
            <Route path="/admin/notifications" element={<AdminRoute><AdminNotifications /></AdminRoute>} />
            <Route path="/admin/store-config" element={<AdminRoute><AdminStoreConfig /></AdminRoute>} />
            <Route path="/admin/bug-reports" element={<AdminRoute><AdminBugReports /></AdminRoute>} />
            <Route path="/admin/billing" element={<AdminRoute><AdminBilling /></AdminRoute>} />
            <Route path="/admin/partner-settlements" element={<AdminRoute><AdminPartnerSettlements /></AdminRoute>} />
            <Route path="/admin/cms" element={<AdminRoute><AdminCMS /></AdminRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </DelayedSuspense>
      )}
    </>
  );
};

const App = () => {
  useEffect(() => {
    // Initial Hydration (M-User Grade Refresh)
    loadCache();
    
    // Subscribe to cache changes for real-time persistence
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.type === 'updated' && event.query.state.status === 'success') {
        saveCache();
      }
    });

    if (Capacitor.isNativePlatform()) {
      try {
        GoogleAuth.initialize({
          clientId: '703263624538-tnagcb97kf682n718ol44lmfdld6pje8.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
        });
      } catch (e) {
        console.error('Failed to initialize GoogleAuth:', e);
      }
    }

    return () => unsubscribe();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <AppRoutes />
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
